import { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { ImageUpload } from '../../components/ui/image-upload';
import { toast } from 'sonner';
import { Settings, Mail, BarChart3, Send, Save, Globe, X, Plus, Users, Handshake, Code, MousePointer, Palette, AlertTriangle, Calendar, FileText } from 'lucide-react';
import api from '../../lib/api';

const defaultSponsorTiers = [
  { name: "Main Partner", weight: 1 },
  { name: "In collaborazione con", weight: 2 },
  { name: "Con il patrocinio di", weight: 3 },
  { name: "Partner", weight: 4 },
  { name: "Special Partner", weight: 5 },
  { name: "Smart Partner", weight: 6 },
  { name: "Grazie a", weight: 7 }
];

export const SettingsAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    sendgrid_from_email: '',
    sendgrid_from_name: '',
    analytics_tracking_id: '',
    analytics_enabled: false,
    contact_email: '',
    contact_phone: '',
    // SEO
    seo_site_title: '',
    seo_site_image: '',
    seo_keywords: [],
    // Favicon
    favicon_url: '',
    // Sponsor tiers
    sponsor_tiers: defaultSponsorTiers,
    // Partner form
    partner_form_html: '',
    partner_contact_mode: 'chatbot',
    // Custom code injection
    custom_head_code: '',
    custom_footer_code: '',
    // Navbar CTA Button
    navbar_cta_enabled: false,
    navbar_cta_text: '',
    navbar_cta_url: '',
    navbar_cta_color: '#7B2CBF',
    // Maintenance mode
    maintenance_mode: false,
    maintenance_title: 'Sito in Manutenzione',
    maintenance_message: 'Stiamo lavorando per migliorare la tua esperienza. Torna a trovarci presto!',
    maintenance_end_date: '',
    // Legal pages
    privacy_policy_url: '',
    cookie_policy_url: '',
    cookie_settings_html: '',
  });
  const [seoKeywordInput, setSeoKeywordInput] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/settings');
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      // Settings might not exist yet, that's ok
      console.log('No settings found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/settings', settings);
      toast.success('Impostazioni salvate con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio delle impostazioni');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Settings className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Settings</h1>
          <p className="text-muted-foreground">Configura le impostazioni della piattaforma</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="rounded-full bg-primary hover:bg-primary/90"
          data-testid="save-settings-btn"
        >
          <Save size={18} className="mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SendGrid Settings */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Send className="text-blue-500" size={20} />
              </div>
              <div>
                <CardTitle className="text-lg">SendGrid</CardTitle>
                <CardDescription>Configurazione email transazionali</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-4">
              <p className="text-xs text-blue-400">
                <strong>Nota:</strong> L'API Key di SendGrid viene letta dal file .env del server (SENDGRID_API_KEY)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Email Mittente</Label>
              <Input
                type="email"
                value={settings.sendgrid_from_email}
                onChange={(e) => updateSetting('sendgrid_from_email', e.target.value)}
                placeholder="noreply@sios.it"
                className="bg-white/5 border-white/10"
                data-testid="input-sendgrid-from-email"
              />
            </div>

            <div className="space-y-2">
              <Label>Nome Mittente</Label>
              <Input
                value={settings.sendgrid_from_name}
                onChange={(e) => updateSetting('sendgrid_from_name', e.target.value)}
                placeholder="SIOS - StartupItalia Open Summit"
                className="bg-white/5 border-white/10"
                data-testid="input-sendgrid-from-name"
              />
            </div>
          </CardContent>
        </Card>

        {/* Analytics Settings */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <BarChart3 className="text-orange-500" size={20} />
              </div>
              <div>
                <CardTitle className="text-lg">Analytics</CardTitle>
                <CardDescription>Google Analytics / Tag Manager</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tracking ID / Measurement ID</Label>
              <Input
                value={settings.analytics_tracking_id}
                onChange={(e) => updateSetting('analytics_tracking_id', e.target.value)}
                placeholder="G-XXXXXXXXXX o UA-XXXXXXXX-X"
                className="bg-white/5 border-white/10"
                data-testid="input-analytics-tracking-id"
              />
              <p className="text-xs text-muted-foreground">
                Inserisci il Measurement ID di Google Analytics 4 (G-XXXX) o il Tracking ID di Universal Analytics (UA-XXXX)
              </p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="analytics-enabled"
                checked={settings.analytics_enabled}
                onChange={(e) => updateSetting('analytics_enabled', e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
                data-testid="checkbox-analytics-enabled"
              />
              <Label htmlFor="analytics-enabled" className="cursor-pointer">
                Abilita Analytics sul sito pubblico
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Contact Settings */}
        <Card className="bg-card border-white/5 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Mail className="text-green-500" size={20} />
              </div>
              <div>
                <CardTitle className="text-lg">Contatti</CardTitle>
                <CardDescription>Informazioni di contatto visualizzate sul sito</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email di Contatto</Label>
                <Input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => updateSetting('contact_email', e.target.value)}
                  placeholder="info@sios.it"
                  className="bg-white/5 border-white/10"
                  data-testid="input-contact-email"
                />
                <p className="text-xs text-muted-foreground">
                  Email visualizzata nel footer e nelle pagine di contatto
                </p>
              </div>

              <div className="space-y-2">
                <Label>Telefono (opzionale)</Label>
                <Input
                  type="tel"
                  value={settings.contact_phone}
                  onChange={(e) => updateSetting('contact_phone', e.target.value)}
                  placeholder="+39 02 1234567"
                  className="bg-white/5 border-white/10"
                  data-testid="input-contact-phone"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SEO Settings */}
        <Card className="bg-card border-white/5 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Globe className="text-purple-500" size={20} />
              </div>
              <div>
                <CardTitle className="text-lg">SEO</CardTitle>
                <CardDescription>Configurazione meta tag e parole chiave per i motori di ricerca</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Titolo del Sito</Label>
                  <Input
                    value={settings.seo_site_title || ''}
                    onChange={(e) => updateSetting('seo_site_title', e.target.value)}
                    placeholder="SIOS - StartupItalia Open Summit"
                    className="bg-white/5 border-white/10"
                    data-testid="input-seo-site-title"
                  />
                  <p className="text-xs text-muted-foreground">
                    Il titolo apparirà come prefisso in tutte le pagine: "Titolo Sito - Titolo Pagina"
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Parole Chiave del Sito</Label>
                  <div className="flex gap-2">
                    <Input
                      value={seoKeywordInput}
                      onChange={(e) => setSeoKeywordInput(e.target.value)}
                      placeholder="Aggiungi parola chiave..."
                      className="bg-white/5 border-white/10"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && seoKeywordInput.trim()) {
                          e.preventDefault();
                          const newKeywords = [...(settings.seo_keywords || []), seoKeywordInput.trim()];
                          updateSetting('seo_keywords', newKeywords);
                          setSeoKeywordInput('');
                        }
                      }}
                      data-testid="input-seo-keyword"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (seoKeywordInput.trim()) {
                          const newKeywords = [...(settings.seo_keywords || []), seoKeywordInput.trim()];
                          updateSetting('seo_keywords', newKeywords);
                          setSeoKeywordInput('');
                        }
                      }}
                      data-testid="add-seo-keyword-btn"
                    >
                      Aggiungi
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(settings.seo_keywords || []).map((keyword, index) => (
                      <span
                        key={`seo-kw-${keyword}`}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/20 text-primary text-sm"
                      >
                        {keyword}
                        <button
                          type="button"
                          onClick={() => {
                            const newKeywords = settings.seo_keywords.filter((_, i) => i !== index);
                            updateSetting('seo_keywords', newKeywords);
                          }}
                          className="hover:text-white"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Queste parole chiave saranno incluse in tutte le pagine del sito
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Immagine di Condivisione (og:image)</Label>
                <ImageUpload
                  value={settings.seo_site_image || ''}
                  onChange={(url) => updateSetting('seo_site_image', url)}
                />
                <p className="text-xs text-muted-foreground">
                  Immagine predefinita mostrata quando il sito viene condiviso sui social
                </p>
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 mt-2">
                  <p className="text-xs text-green-400">
                    ✓ L'immagine viene automaticamente ottimizzata (1200x630px, &lt;200KB) per una visualizzazione perfetta su WhatsApp, Facebook, Twitter e LinkedIn.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Favicon</Label>
                <ImageUpload
                  value={settings.favicon_url || ''}
                  onChange={(url) => updateSetting('favicon_url', url)}
                  acceptIco={true}
                />
                <p className="text-xs text-muted-foreground">
                  Icona mostrata nella tab del browser. Consigliato: formato .ico, .png o .svg (32x32 o 16x16 pixel)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Partner Contact Mode */}
        <Card className="glass border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Handshake className="text-purple-400" size={20} />
              </div>
              <div>
                <CardTitle>Pagina Diventa Partner</CardTitle>
                <CardDescription>Scegli come gli utenti possono contattarti per diventare partner</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Label>Modalità di contatto</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => updateSetting('partner_contact_mode', 'chatbot')}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    (settings.partner_contact_mode || 'chatbot') === 'chatbot'
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid="partner-mode-chatbot"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Users size={20} className="text-primary" />
                    <span className="font-semibold">Chatbot AI</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Assistente virtuale che risponde alle domande sui pacchetti di sponsorizzazione
                  </p>
                </button>
                
                <button
                  type="button"
                  onClick={() => updateSetting('partner_contact_mode', 'form')}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    settings.partner_contact_mode === 'form'
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid="partner-mode-form"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Mail size={20} className="text-primary" />
                    <span className="font-semibold">Form Contatto</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Form HTML personalizzato o embed di servizi esterni (HubSpot, Typeform, ecc.)
                  </p>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Partner Form HTML Card - shown only if form mode is selected */}
        {settings.partner_contact_mode === 'form' && (
        <Card className="glass border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Code className="text-purple-400" size={20} />
              </div>
              <div>
                <CardTitle>Form Diventa Partner</CardTitle>
                <CardDescription>Inserisci il codice HTML del form di contatto per la pagina "Diventa Partner"</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Codice HTML del Form</Label>
                <textarea
                  value={settings.partner_form_html || ''}
                  onChange={(e) => updateSetting('partner_form_html', e.target.value)}
                  placeholder="<form>...</form> oppure codice embed (es. HubSpot, Typeform, Google Forms)"
                  className="w-full h-64 p-4 rounded-lg bg-white/5 border border-white/10 font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="input-partner-form-html"
                />
                <p className="text-xs text-muted-foreground">
                  Puoi inserire codice HTML personalizzato o un embed di servizi come HubSpot, Typeform, Google Forms, ecc.
                </p>
              </div>
              
              {settings.partner_form_html && (
                <div className="space-y-2">
                  <Label>Anteprima</Label>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 overflow-auto max-h-64">
                    <iframe
                      srcDoc={settings.partner_form_html}
                      sandbox="allow-scripts allow-forms allow-same-origin"
                      className="partner-form-preview w-full min-h-[200px] border-0"
                      title="Anteprima form partner"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Custom Code Injection */}
        <Card className="bg-card border-white/5 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Code className="text-cyan-500" size={20} />
              </div>
              <div>
                <CardTitle>Codice Personalizzato</CardTitle>
                <CardDescription>Inserisci script JS o codice HTML da iniettare nell'head o nel footer del sito</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Head Code */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span>Codice Head</span>
                    <span className="text-xs text-muted-foreground font-normal">(inserito prima di &lt;/head&gt;)</span>
                  </Label>
                  <textarea
                    value={settings.custom_head_code || ''}
                    onChange={(e) => updateSetting('custom_head_code', e.target.value)}
                    placeholder={`<!-- Google Analytics, Meta Pixel, etc. -->\n<script>\n  // Il tuo codice JS qui\n</script>`}
                    className="w-full h-48 p-4 rounded-lg bg-white/5 border border-white/10 font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
                    data-testid="input-custom-head-code"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ideale per: Google Tag Manager, Meta Pixel, script di tracking, meta tag personalizzati
                  </p>
                </div>
              </div>

              {/* Footer Code */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span>Codice Footer</span>
                    <span className="text-xs text-muted-foreground font-normal">(inserito prima di &lt;/body&gt;)</span>
                  </Label>
                  <textarea
                    value={settings.custom_footer_code || ''}
                    onChange={(e) => updateSetting('custom_footer_code', e.target.value)}
                    placeholder={`<!-- Chat widget, cookie banner, etc. -->\n<script>\n  // Il tuo codice JS qui\n</script>`}
                    className="w-full h-48 p-4 rounded-lg bg-white/5 border border-white/10 font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
                    data-testid="input-custom-footer-code"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ideale per: Chat widget (Tawk.to, Crisp), cookie consent, script che devono caricarsi dopo il DOM
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400">
                <strong>Attenzione:</strong> Assicurati che il codice inserito sia sicuro e provenga da fonti affidabili. 
                Script malevoli potrebbero compromettere la sicurezza del sito.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Navbar CTA Button */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <MousePointer className="text-pink-500" size={20} />
              </div>
              <div>
                <CardTitle>Bottone Navbar</CardTitle>
                <CardDescription>Configura un pulsante CTA visibile nella barra di navigazione</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                <div>
                  <Label className="text-base">Abilita Bottone</Label>
                  <p className="text-sm text-muted-foreground">Mostra il pulsante CTA nella navbar</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.navbar_cta_enabled || false}
                    onChange={(e) => updateSetting('navbar_cta_enabled', e.target.checked)}
                    className="sr-only peer"
                    data-testid="navbar-cta-enabled-toggle"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Button Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Testo del Bottone</Label>
                  <Input
                    value={settings.navbar_cta_text || ''}
                    onChange={(e) => updateSetting('navbar_cta_text', e.target.value)}
                    placeholder="Es: Ottieni il tuo Pass"
                    className="bg-white/5 border-white/10"
                    data-testid="navbar-cta-text-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Link (URL)</Label>
                  <Input
                    value={settings.navbar_cta_url || ''}
                    onChange={(e) => updateSetting('navbar_cta_url', e.target.value)}
                    placeholder="Es: https://eventbrite.com/..."
                    className="bg-white/5 border-white/10"
                    data-testid="navbar-cta-url-input"
                  />
                </div>
              </div>

              {/* Color Picker */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette size={16} />
                  Colore del Bottone
                </Label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={settings.navbar_cta_color || '#7B2CBF'}
                    onChange={(e) => updateSetting('navbar_cta_color', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-white/20"
                    data-testid="navbar-cta-color-picker"
                  />
                  <Input
                    value={settings.navbar_cta_color || '#7B2CBF'}
                    onChange={(e) => updateSetting('navbar_cta_color', e.target.value)}
                    placeholder="#7B2CBF"
                    className="bg-white/5 border-white/10 w-32 font-mono"
                  />
                  <div className="flex gap-2">
                    {['#7B2CBF', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#EC4899'].map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => updateSetting('navbar_cta_color', color)}
                        className="w-8 h-8 rounded-full border-2 border-white/20 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview */}
              {settings.navbar_cta_enabled && settings.navbar_cta_text && (
                <div className="space-y-2">
                  <Label>Anteprima</Label>
                  <div className="p-4 rounded-lg bg-black/30 border border-white/10 flex items-center justify-center">
                    <a 
                      href={settings.navbar_cta_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-2.5 rounded-full font-medium text-white transition-all hover:opacity-90 shadow-lg"
                      style={{ backgroundColor: settings.navbar_cta_color || '#7B2CBF' }}
                    >
                      {settings.navbar_cta_text}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Mode */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="text-red-500" size={20} />
              </div>
              <div>
                <CardTitle>Modalità Manutenzione</CardTitle>
                <CardDescription>Blocca l'accesso al sito pubblico mostrando una pagina di manutenzione</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <div>
                  <Label className="text-base">Attiva Manutenzione</Label>
                  <p className="text-sm text-muted-foreground">
                    Quando attivo, tutte le pagine pubbliche mostreranno la pagina di manutenzione
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.maintenance_mode || false}
                    onChange={(e) => updateSetting('maintenance_mode', e.target.checked)}
                    className="sr-only peer"
                    data-testid="maintenance-mode-toggle"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>

              {settings.maintenance_mode && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-sm text-amber-400 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    <strong>Attenzione:</strong> Il sito è attualmente in modalità manutenzione. Ricordati di salvare le impostazioni e ricaricare la pagina per vedere le modifiche.
                  </p>
                </div>
              )}

              {/* Maintenance Configuration */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Titolo della Pagina</Label>
                  <Input
                    value={settings.maintenance_title || ''}
                    onChange={(e) => updateSetting('maintenance_title', e.target.value)}
                    placeholder="Sito in Manutenzione"
                    className="bg-white/5 border-white/10"
                    data-testid="maintenance-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Messaggio</Label>
                  <textarea
                    value={settings.maintenance_message || ''}
                    onChange={(e) => updateSetting('maintenance_message', e.target.value)}
                    placeholder="Stiamo lavorando per migliorare la tua esperienza. Torna a trovarci presto!"
                    className="w-full h-24 p-4 rounded-lg bg-white/5 border border-white/10 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
                    data-testid="maintenance-message-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar size={16} />
                    Data/Ora Fine Manutenzione (opzionale)
                  </Label>
                  <Input
                    type="datetime-local"
                    value={settings.maintenance_end_date || ''}
                    onChange={(e) => updateSetting('maintenance_end_date', e.target.value)}
                    className="bg-white/5 border-white/10"
                    data-testid="maintenance-end-date-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Se specificata, verrà mostrata agli utenti come stima del ritorno online
                  </p>
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>Anteprima Pagina Manutenzione</Label>
                <div className="p-6 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-white/10 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Settings className="w-8 h-8 text-primary animate-spin" style={{ animationDuration: '3s' }} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {settings.maintenance_title || 'Sito in Manutenzione'}
                  </h3>
                  <p className="text-white/70 text-sm max-w-md mx-auto">
                    {settings.maintenance_message || 'Stiamo lavorando per migliorare la tua esperienza. Torna a trovarci presto!'}
                  </p>
                  {settings.maintenance_end_date && (
                    <p className="text-xs text-primary/80 mt-4">
                      Torneremo online: {new Date(settings.maintenance_end_date).toLocaleString('it-IT')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legal Pages */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Pagine Legali
            </CardTitle>
            <CardDescription>
              Configura i link alle pagine legali che appariranno nel footer del sito
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="privacy_policy_url">URL Privacy Policy</Label>
                <Input
                  id="privacy_policy_url"
                  placeholder="https://esempio.com/privacy-policy"
                  value={settings.privacy_policy_url || ''}
                  onChange={(e) => updateSetting('privacy_policy_url', e.target.value)}
                  className="glass"
                  data-testid="privacy-policy-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Link alla Privacy Policy
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cookie_policy_url">URL Cookie Policy</Label>
                <Input
                  id="cookie_policy_url"
                  placeholder="https://esempio.com/cookie-policy"
                  value={settings.cookie_policy_url || ''}
                  onChange={(e) => updateSetting('cookie_policy_url', e.target.value)}
                  className="glass"
                  data-testid="cookie-policy-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Link alla Cookie Policy
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cookie_settings_html">Codice HTML Impostazioni Privacy e Cookie</Label>
              <Textarea
                id="cookie_settings_html"
                placeholder='<a href="#" class="iubenda-cs-preferences-link">Impostazione privacy e cookie</a>'
                value={settings.cookie_settings_html || ''}
                onChange={(e) => updateSetting('cookie_settings_html', e.target.value)}
                className="glass font-mono text-sm"
                rows={3}
                data-testid="cookie-settings-html-input"
              />
              <p className="text-xs text-muted-foreground">
                Codice HTML per il link alle impostazioni privacy/cookie (es. Iubenda, OneTrust, Cookiebot)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
